import React, { useMemo, useState } from 'react';
import FormButton from 'components/Common/FormButton';
import ProgressMessage from 'components/Utils/ProgressMessage/index';
import SubscriptionUtils from 'services/SubscriptionUtils';
import './style.scss';
import SwipeableViews from 'react-swipeable-views';
import Icon from 'components/Utils/NewIcon';
import { PromoCodeButton } from 'scenes/PatientSubscription/CreditCardScreen';
import ToggleSwitch from 'components/Utils/ToggleSwitch';
import DefaultConfirmationModal from 'components/Utils/DefaultConfirmationScreen';
import PromoCodeModal from 'components/Payments/CreditCardScreen/PromoCodeModal';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppSelector } from 'careand-redux/store';
import { Portal } from '@material-ui/core';

const SubscriptionView = (props) => (
  <div className="subscription safe-area-bottom">
    <LoadingPlan {...props} />
    <AllPlans {...props} />
    <PromoCodeModal {...props} />
  </div>
);

const LoadingPlan = (props) => (
  <ProgressMessage message="Loading subscriptions..." show={props.plans.length === 0} />
);

export const AllPlans = (props) => {
  const plans = props.plans;
  const [interval, setInterval] = useState('year');
  const hasMonthlyPlan = plans.some((plan) => plan.interval === 'month');

  const [showMessage, setShowMessage] = useState(undefined);

  const filteredPlans = useMemo(() => {
    return plans.filter((plan) => plan.interval === interval || plan.id === 'unsubscribed');
  }, [plans, interval]);

  function onChoosePlanWrapper(plan) {
    if (SubscriptionUtils.getMinimumMonths(plan) > 0) {
      setShowMessage(plan);
    } else {
      props.onChoosePlan(plan);
    }
  }

  const showMessageMonths = SubscriptionUtils.getMinimumMonths(showMessage);

  return (
    plans.length > 0 && (
      <>
        {!!showMessage && (
          <DefaultConfirmationModal
            title="Confirmation Needed"
            open={!!showMessage}
            icon="credit-card"
            text={`Subscriptions with a monthly billing cycle require a minimum commitment of ${showMessageMonths} months. Are you sure you want to proceed with a monthly payment plan?`}
            onConfirm={() => props.onChoosePlan(showMessage)}
            confirmLabel={`Yes, I understand there is a minimum commitment of ${showMessageMonths} months`}
            cancelLabel="No, I prefer an annual billing cycle"
            onCancel={() => setShowMessage()}
          />
        )}
        <div className="toggle-plans-wrapper">
          {hasMonthlyPlan && (
            <div className="monthly-toggle">
              <ToggleSwitch
                option1="month"
                element1="Monthly Billing"
                option2="year"
                element2="Annual Billing"
                onChange={setInterval}
                toggled={interval === 'year'}
              />
            </div>
          )}
          <div className="plans-wrapper">
            <div className="mobile-only">
              <SwipeableViews
                style={{ padding: '0 30px 0 30px', height: '100%' }}
                containerStyle={{ height: '100%' }}
                slideStyle={{ padding: '10px 15px 10px 15px' }}
              >
                {filteredPlans.map((plan) => (
                  <PlanDetails
                    {...props}
                    key={plan?.id}
                    plan={plan}
                    isFeatured={!!plan.highlight}
                    onChoosePlan={onChoosePlanWrapper}
                  />
                ))}
              </SwipeableViews>
            </div>
            <div className="desktop-only">
              {filteredPlans.map((plan) => (
                <PlanDetails
                  {...props}
                  key={plan?.id}
                  plan={plan}
                  isFeatured={!!plan.highlight}
                  onChoosePlan={onChoosePlanWrapper}
                />
              ))}
            </div>
          </div>
        </div>
        {interval === 'year' && (
          <div className="row promo-code-wrapper">
            {!!props.openPromoCodeModal ? (
              <PromoCodeButton
                onClick={() => props.openPromoCodeModal(true)}
                className="blue"
                plan={plans[1]}
              />
            ) : null}
          </div>
        )}
      </>
    )
  );
};
export const PlanDetails = ({ plan = {}, isFeatured, ...props }) => {
  let className = 'plan-box';
  if (isFeatured) {
    className += ' is-featured';
  }

  let minimumMonths = SubscriptionUtils.getMinimumMonths(plan);

  return (
    <div className={className} id={'plan-' + plan.id}>
      <span className="plan-name">{plan.name}</span>
      <div className="plan-card">
        {isFeatured && (
          <div className="most-popular-badge">
            <div className="badge-content">MOST POPULAR</div>
          </div>
        )}
        <div className="plan-table-buttons">
          <PlanTable plan={plan} />
          <Price plan={plan} isFeatured={isFeatured} minimumMonths={minimumMonths} {...props} />
        </div>
      </div>
    </div>
  );
};

const PlanTable = ({ plan }) => {
  const features = plan.features || [];

  return (
    <div className="plan-table">
      {features.map((feature) => (
        <Row key={feature} text={feature} />
      ))}
    </div>
  );
};

const Row = ({ text }) => (
  <div className="row">
    <div className="cell">{text}</div>
    <div className="cell">
      <div className="subscription-checkmark is-colored">
        <Icon src="check" alt="Included" height="14px" width="14px" />
      </div>
    </div>
  </div>
);

const Price = ({ plan, isFeatured, minimumMonths, ...props }) => {
  return (
    <>
      <div className="price-box">
        <SubscriptionFee plan={plan} minimumMonths={minimumMonths} />
      </div>
      <div className="buttons">
        {!props.button && <SubscribeButton plan={plan} {...props} isLight={isFeatured} />}
        {!!props.button && props.button}
      </div>
    </>
  );
};

const SubscribeButton = ({ plan, subscription, onChoosePlan, loading, isLight, ...props }) => {
  const dispatch = useAppDispatch();
  const patientId = useAppSelector((state) => state.authentication.activeUserId);
  const [reactivating, setReactivating] = useState(false);

  let name = 'Select Plan';
  let disabled = false;
  const isScheduledToCancel = SubscriptionUtils.isScheduledToCancel(subscription);
  const isCurrentPlan = plan.id === subscription?.plan?.id;

  if (isCurrentPlan && !isScheduledToCancel) {
    name = 'Already Subscribed';
    disabled = true;
  }

  if (isCurrentPlan && isScheduledToCancel) {
    name = 'Reactivate Subscription';
    disabled = false;
  }

  if (plan.id === 'unsubscribed') {
    name = 'Book Now';
    disabled = false;
  }

  async function handleReactivate() {
    setReactivating(true);
    await dispatch(api.Payments.reactivateSubscription({ userId: patientId }), {
      errorTitle: 'Reactivate Subscription',
      successText: 'Subscription reactivated successfully!',
    });
    setReactivating(false);
  }

  function handleClick() {
    if (isCurrentPlan && isScheduledToCancel) {
      handleReactivate();
    } else {
      onChoosePlan(plan);
    }
  }

  let className = 'subscribe-button-wrapper';
  if (disabled) {
    className += ' is-disabled';
  }

  return (
    <>
      <div className={className}>
        {disabled ? (
          name
        ) : (
          <FormButton
            {...props}
            id={'subscription-continue-btn-' + plan.id}
            name={name}
            style={{}}
            className={isLight ? 'light' : 'blue'}
            disabled={disabled}
            onClick={handleClick}
            loading={loading || reactivating}
          />
        )}
      </div>
    </>
  );
};

const SubscriptionFee = ({ plan, minimumMonths }) => {
  let interval = plan.interval === 'year' ? 'billed annually' : 'billed monthly';

  let discount = plan.discount;
  let amount = plan.amount;

  if (discount > 0) discount = amount - discount;

  let isPayPerUse = plan.id === 'unsubscribed';

  return (
    <div>
      <div className="fee">
        {isPayPerUse && (
          <div className="pay-per-use-fee">
            <div className="price-row">
              <span className="amount">Pay per visit</span>
            </div>
            <span>priced at booking, per person</span>
          </div>
        )}
        {amount > 0 && (
          <>
            {!!discount && (
              <>
                <span className="discounted-fee">
                  CAD ${SubscriptionUtils.convertPlanAmount(amount)} (+HST)
                </span>
                <div className="price-row">
                  <span className="amount">
                    CAD ${SubscriptionUtils.convertPlanAmount(discount)}
                  </span>
                  <span className="hst">(+HST) *</span>
                </div>
              </>
            )}
            {!discount && (
              <div className="price-row">
                <span className="amount">CAD ${SubscriptionUtils.convertPlanAmount(amount)}</span>
                <span className="hst">(+HST)</span>
              </div>
            )}
            <span>per person {interval}</span>
            {!!discount && (
              <span className="asterisk-text">* promotional price for first year of service</span>
            )}
            {minimumMonths > 0 && (
              <span className="bold">Minimum commitment: {minimumMonths} months</span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionView;
